import { useQuery } from '@apollo/react-hooks';
import { AuthContext } from '@kargotech/tms-core/auth';
import { intersection } from 'lodash-es';
import React, {
  createContext, useCallback, useContext, useState,
} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ApolloQueryResult } from 'apollo-client';

import { USER_ACCESS_TYPE } from '~/configurations/constants';
import PROFILE from '~/graphql/profile/queries/profile.profile';
import { getUserProfile } from '~/models/userProfile';
import sentry from '~/services/sentry';
import gtag from '~/services/google-analytics';
import { APOLLO_CLIENTS } from '~/services/apollo';

const EMPTY_USER_PROFILE = getUserProfile(null);

type EmptyUserProfileType = typeof EMPTY_USER_PROFILE;

type ProfileCompany = typeof EMPTY_USER_PROFILE.company;

interface ICompany extends ProfileCompany {
  ksuid: string;
}

export interface IProfileContext {
  getCompanyMetadata: unknown;
  getProfile: (variables?: {
    companyKsuid: string;
  }) => Promise<ApolloQueryResult<unknown>>;
  isGetProfileLoading?: boolean;
  getSelectedCompany: () => ICompany;
  isAuthorizedToAccess: (roles: string[]) => boolean;
  profile: EmptyUserProfileType;
  resetProfile: () => void;
}

export const ProfileContext = createContext<IProfileContext>({} as IProfileContext);

function ProfileProvider({ children }) {
  const { selectedCompanyKsuid } = useContext(AuthContext);
  const [profile, setProfile] = useState(EMPTY_USER_PROFILE);
  const [isGetProfileLoading, setIsGetProfileLoading] = useState(true);

  const resetProfile = useCallback(() => {
    setProfile(EMPTY_USER_PROFILE);
    setIsGetProfileLoading(false);
  }, []);

  const getSelectedCompany = useCallback(() => {
    const { ksuid, ...restProfileAttrrs } = profile?.company || {};
    return {
      ksuid: selectedCompanyKsuid || ksuid,
      ...restProfileAttrrs,
    } as ICompany;
  }, [profile, selectedCompanyKsuid]);

  const { refetch: getProfile } = useQuery(PROFILE, {
    client: APOLLO_CLIENTS.PROFILE,
    fetchPolicy: 'network-only',
    skip: !selectedCompanyKsuid,
    notifyOnNetworkStatusChange: true,
    variables: {
      companyKsuid: selectedCompanyKsuid,
    },
    onCompleted: (profileData) => {
      const formattedProfileData = getUserProfile(profileData);
      setProfile(formattedProfileData);
      setIsGetProfileLoading(false);

      // set sentry user context
      sentry.setUser({
        id: formattedProfileData.ksuid,
        companyId: formattedProfileData.company.ksuid,
        email: formattedProfileData.email,
        phone: formattedProfileData.phoneNumber,
      });

      gtag('set', { user_id: formattedProfileData.ksuid });
    },
    onError: resetProfile,
  });

  const getCompanyMetadata = useCallback(() => {
    const { metadata } = getSelectedCompany();
    if (!metadata || typeof metadata !== 'string') {
      return undefined;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(metadata);
    } catch {
      return undefined;
    }
  }, [getSelectedCompany]);

  /**
   * Check logged-in user roles are matching with allowed roles.
   * Logged-in user roles is stored in localStorage under selectedCompany.accessTypes property.
   * Allowed roles should be any value from ACCESS_PRIVILEGE.
   * The logged-in user roles will be intersecting with the provided Allowed roles excluding RESTRICTED_ACCESS
   * User is authorized if there's one or more matching roles
   * @require src/configurations/rbac.js#ACCESS_PRIVILEGE
   * @example
   * isAuthorizedToAccess(ACCESS_PRIVILEGE.SHIPMENT_READ);
   * @param {String[]} roles must-have roles
   * @returns {Boolean} is user authorized or not
   */
  const isAuthorizedToAccess = useCallback(
    (roles: string[] = []) => {
      const allowedRoles = roles.filter((role) => role !== USER_ACCESS_TYPE.RESTRICTED_ACCESS);
      const userAccessTypes = getSelectedCompany()
        .accessTypes?.filter((accessType) => accessType !== USER_ACCESS_TYPE.RESTRICTED_ACCESS) || [];

      return !!intersection(allowedRoles, userAccessTypes).length;
    },
    [getSelectedCompany],
  );

  return (
    <ProfileContext.Provider
      value={{
        profile,
        getProfile,
        isGetProfileLoading,
        resetProfile,
        getSelectedCompany,
        isAuthorizedToAccess,
        getCompanyMetadata,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export default ProfileProvider;
